import { Button, Flex, Heading } from '@chakra-ui/react';
import { FormInput, FormRadioButtons, toaster, type ConfirmModalHandle } from '@shared';
import type { RefObject } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAddCredits, useRemoveCredits } from '../../api/hooks/useAccountSuperadmin';
import { useQueryClient } from '@tanstack/react-query';

type ModifyCreditsForm = {
	action: 'add' | 'remove';
	creditCount: number;
};

export default function ModifyAccountCreditsModal({ credits, accountID, ref }: { credits: number; accountID: number; ref: RefObject<ConfirmModalHandle | null> }) {
	const { t } = useTranslation();
	const { mutate: addCredits, isPending: isAddingCredits } = useAddCredits();
	const { mutate: removeCredits, isPending: isRemovingCredits } = useRemoveCredits();
	const queryClient = useQueryClient();

	const methods = useForm<ModifyCreditsForm>();
	const { handleSubmit, setValue } = methods;

	function modifyCredits(values: ModifyCreditsForm): void {
		const payload = {
			accountID: accountID,
			creditCount: values.creditCount,
		};

		const closeRef = () => {
			setTimeout(() => ref.current?.close(), 0);
		};

		if (values.action === 'add') {
			addCredits(payload, {
				onSuccess: () => {
					toaster.create({
						type: 'success',
						title: t('general.credits_added'),
					});
					queryClient.invalidateQueries({ queryKey: ['accounts'] });
					closeRef();
				},
			});
		} else {
			if (payload.creditCount > credits) {
				payload.creditCount = credits;
			}
			removeCredits(payload, {
				onSuccess: () => {
					toaster.create({
						type: 'success',
						title: t('general.credits_removed'),
					});
					queryClient.invalidateQueries({ queryKey: ['accounts'] });
					closeRef();
				},
			});
		}
	}

	return (
		<form onSubmit={e => handleSubmit(modifyCredits)(e)}>
			<FormProvider {...methods}>
				<Flex flexDirection={'column'} gap={4}>
					<Heading size="sm" marginBottom={4}>
						{t('general.credits_at_the_moment')}: {credits}
					</Heading>

					<FormRadioButtons
						isRequired
						label={''}
						name="alwaysValid"
						registerOptions={{
							onChange: e => {
								setValue('action', e.target.value, {
									shouldDirty: true,
								});
							},
						}}
						options={[
							{
								label: t('general.add_credits'),
								value: 'add',
							},
							{
								label: t('general.remove_credits'),
								value: 'remove',
							},
						]}
					/>
					<FormInput isRequired min={1} label={t('general.amount')} name="creditCount" type="number" />
					<Flex gap={2} marginTop={4} marginLeft="auto">
						<Button
							onClick={() => {
								ref.current?.close();
							}}
							variant="outline"
						>
							{t('general.cancel')}
						</Button>
						<Button disabled={isAddingCredits || isRemovingCredits} type="submit">
							{t('general.save')}
						</Button>
					</Flex>
				</Flex>
			</FormProvider>
		</form>
	);
}
